import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  ApiStoreList,
  ApiStoreDetail,
  ApiPhysicalStoresOptions,
  ApiPhysicalStores
} from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiRequestService } from 'idt-ecommerce-api/_api/api-util/api-request.service';
import { RenderSetupDataService } from 'idt-ecommerce-api/render-setup-data/render-setup-data.service';
import { DEFAULT_LANGUAGE_ID } from 'idt-ecommerce-api/stores/stores.models';


const STORES_CACHE_KEY = 'iStoresJSON';
const STORE_DETAIL_CACHE_KEY = 'iStoreJSON';

const readSessionStorage = <T>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch (error) {
    console.warn(`ApiStoresService cache: failed to read "${key}" from sessionStorage`, error);
    return null;
  }
};

const writeSessionStorage = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`ApiStoresService cache: failed to write "${key}" to sessionStorage`, error);
  }
};

export interface ApiStoresServiceInterface {
  stores(languageId: string): Observable<ApiStoreList>;
  storeDetail(storeId: string, languageId: string): Observable<ApiStoreDetail>;
  physicalStores(storeId: string, languageId: string, options: ApiPhysicalStoresOptions): Observable<ApiPhysicalStores>;
  retrieveDefaultStoreIdAndLanguageId(
    countryCode: string, languageCode: string
  ): Observable<{ storeId?: string; languageId: string }>;
}

@Injectable()
export class ApiStoresService implements ApiStoresServiceInterface {

  constructor(
    private readonly renderSetupDataService: RenderSetupDataService,
    private readonly http: HttpClient,
    private readonly requestService: ApiRequestService
  ) {}

  public stores(languageId: string): Observable<ApiStoreList> {
    if (this.renderSetupDataService.stores) {
      return of(this.renderSetupDataService.stores);
    }

    const storesCached = readSessionStorage<ApiStoreList>(STORES_CACHE_KEY);
    if (storesCached) {
      return of(storesCached);
    }

    return this.requestService.brandId().pipe(
      map((brandId: string) => [{ key: 'brandId', value: brandId }]),
      switchMap((customParams) => this.requestService.requestUrl('catalog/store', languageId, customParams)),
      switchMap((url: string) => this.http.get<ApiStoreList>(url)),
      tap((stores) => writeSessionStorage(STORES_CACHE_KEY, stores))
    );
  }

  public storeDetail(storeId: string, languageId?: string): Observable<ApiStoreDetail> {
    if (this.renderSetupDataService.store) {
      return of(this.renderSetupDataService.store);
    }

    const storeDetailCached = readSessionStorage<ApiStoreDetail>(STORE_DETAIL_CACHE_KEY);
    if (storeDetailCached) {
      return of(storeDetailCached);
    }

    return this.requestService.requestUrl(`catalog/store/${storeId}`, languageId).pipe(
      switchMap((url: string) => this.http.get<ApiStoreDetail>(url))
    );
  }

  public physicalStores(
    storeId: string,
    languageId: string,
    options: ApiPhysicalStoresOptions
  ): Observable<ApiPhysicalStores> {
    const customParams = Object.entries(options).map((option) => ({ key: option[0], value: option[1] }));

    return this.requestService.requestUrl(`bam/store/${storeId}/physical-store`, languageId, customParams).pipe(
      switchMap((url: string) => this.http.get<ApiPhysicalStores>(url))
    );
  }

  public retrieveDefaultStoreIdAndLanguageId(
    countryCode: string,
    languageCode?: string
  ): Observable<{ storeId?: string; languageId: string }> {
    return this.stores(DEFAULT_LANGUAGE_ID).pipe(
      map((stores) => {
        const defaultStore = stores.stores.find((store) => store.countryCode === countryCode);
        const defaultLanguage = defaultStore?.supportedLanguages.find(
          (lang) => lang.code === languageCode
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        ) ?? defaultStore?.supportedLanguages[0]!;
        return { storeId: defaultStore?.id.toString(), languageId: defaultLanguage?.id.toString() };
      })
    );
  }

}
